<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import type { CreateCorporateData, Contact } from '../types/contact.types';

interface Props {
    modelValue?: CreateCorporateData | Contact;
    readonly?: boolean;
    readonlyFields?: string[];
}

const props = withDefaults(defineProps<Props>(), {
    readonly: false,
    readonlyFields: () => []
});

const emit = defineEmits<{
    'update:modelValue': [value: CreateCorporateData | Contact];
}>();

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);

onMounted(() => {
    window.addEventListener('language-changed', (event: any) => {
        currentLanguage.value = event.detail.language;
    });
});

// snake_case 필드로 변환하여 사용
const legalData = computed({
    get: () => {
        const value = props.modelValue || {} as any;
        
        return {
            LEGAL_FORM: value.LEGAL_FORM || value.legalForm || '',
            INCORPORATION_STATUTE: value.INCORPORATION_STATUTE || value.incorporationStatute || '',
            CORPORATE_ENTITY_REGISTER: value.CORPORATE_ENTITY_REGISTER || value.corporateEntityRegister || '',
        };
    },
    set: (value) => {
        if (props.readonly) return;
        const currentModelValue = (props.modelValue || {}) as any;
        const updated = {
            ...currentModelValue,
            LEGAL_FORM: value.LEGAL_FORM,
            legalForm: value.LEGAL_FORM, // Create 페이지용
            INCORPORATION_STATUTE: value.INCORPORATION_STATUTE,
            incorporationStatute: value.INCORPORATION_STATUTE, // Create 페이지용
            CORPORATE_ENTITY_REGISTER: value.CORPORATE_ENTITY_REGISTER,
            corporateEntityRegister: value.CORPORATE_ENTITY_REGISTER, // Create 페이지용
        };
        emit('update:modelValue', updated as CreateCorporateData | Contact);
    }
});

// REGISTER_TYPE이 A06001인지 확인
const isBizFileRegistrant = computed(() => {
    const value = props.modelValue || {} as any;
    const registerType = String(value.CONTACT_REGISTER_TYPE || value.contactRegisterType || '').trim().toUpperCase();
    return registerType === 'A06001';
});

// readonly 필드인지 확인하는 헬퍼 함수
// Legal Information 필드는 항상 수정 가능
const isReadonlyField = (fieldName: string) => {
    return false;
};
</script>

<template>
    <v-card class="contact-legal-info-section">
        <v-card-title class="contact-section-title">
            {{ t('contact.Legal Information') || 'Legal Information' }}
        </v-card-title>
        <v-card-text>
            <v-row>
                <!-- Left Column -->
                <v-col cols="12" md="6">
                    <v-text-field
                        :model-value="legalData.LEGAL_FORM"
                        :label="t('contact.Legal Form') || 'Legal Form'"
                        variant="outlined"
                        density="comfortable"
                        @update:model-value="(val: any) => {
                            const currentValue = props.modelValue || {} as any;
                            const updated = {
                                ...currentValue,
                                LEGAL_FORM: val ?? '',
                                legalForm: val ?? '' // Create 페이지용
                            };
                            emit('update:modelValue', updated);
                        }"
                    />
                </v-col>
                
                <!-- Right Column -->
                <v-col cols="12" md="6">
                    <v-text-field
                        :model-value="legalData.INCORPORATION_STATUTE"
                        :label="t('contact.Incorporation Statute') || 'Incorporation Statute'"
                        variant="outlined"
                        density="comfortable"
                        @update:model-value="(val: any) => {
                            const currentValue = props.modelValue || {} as any;
                            const updated = {
                                ...currentValue,
                                INCORPORATION_STATUTE: val ?? '',
                                incorporationStatute: val ?? '' // Create 페이지용
                            };
                            emit('update:modelValue', updated);
                        }"
                    />
                </v-col>
            </v-row>
            <v-row>
                <!-- Corporate Entity Register -->
                <v-col cols="12" md="6">
                    <v-text-field
                        :model-value="legalData.CORPORATE_ENTITY_REGISTER"
                        :label="t('contact.Corporate Entity Register') || 'Corporate Entity Register'"
                        variant="outlined"
                        density="comfortable"
                        @update:model-value="(val: any) => {
                            const currentValue = props.modelValue || {} as any;
                            const updated = {
                                ...currentValue,
                                CORPORATE_ENTITY_REGISTER: val ?? '',
                                corporateEntityRegister: val ?? '' // Create 페이지용
                            };
                            emit('update:modelValue', updated);
                        }"
                    />
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<style scoped lang="scss">
@import '../scss/contact.scss';

.contact-legal-info-section {
    background-color: rgb(var(--v-theme-surface)) !important;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
}

/* 폰트 패밀리 */
* {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.contact-section-title {
    font-size: 18px;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    padding: 16px 24px;
    border-bottom: 1px solid #ebf1f6;
}

/* Readonly 필드 스타일 (회색 배경) */
.readonly-field {
    opacity: 1 !important;
}

.readonly-field :deep(.v-field) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-input__control) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field__overlay) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(input),
.readonly-field :deep(textarea) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    cursor: not-allowed !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field--variant-outlined) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field--variant-outlined .v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field.v-text-field--disabled,
.readonly-field.v-select--disabled,
.readonly-field.v-autocomplete--disabled {
    opacity: 1 !important;
}

.readonly-field.v-text-field--disabled :deep(.v-field),
.readonly-field.v-text-field--disabled :deep(.v-field__input),
.readonly-field.v-text-field--disabled :deep(.v-input__control),
.readonly-field.v-text-field--disabled :deep(.v-field__overlay),
.readonly-field.v-text-field--disabled :deep(input),
.readonly-field.v-text-field--disabled :deep(textarea),
.readonly-field.v-select--disabled :deep(.v-field),
.readonly-field.v-select--disabled :deep(.v-field__input),
.readonly-field.v-select--disabled :deep(.v-input__control),
.readonly-field.v-select--disabled :deep(.v-field__overlay),
.readonly-field.v-autocomplete--disabled :deep(.v-field),
.readonly-field.v-autocomplete--disabled :deep(.v-field__input),
.readonly-field.v-autocomplete--disabled :deep(.v-input__control),
.readonly-field.v-autocomplete--disabled :deep(.v-field__overlay) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
    opacity: 1 !important;
}
</style>

